import { VCData } from '../types/vc';

interface VCCardProps {
  vc: VCData;
  onClick: () => void;
}

function getLogoUrl(domain: string) {
  return `https://logo.clearbit.com/${domain}`;
}

function getFallbackInitials(name: string) {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function VCCard({ vc, onClick }: VCCardProps) {
  if (!vc.data || !vc.bdRaw) return null;

  const {
    name,
    data: { imageUrl, website, description },
    bdRaw: { investor_type, investment_stage, num_investments, num_exits },
    sectors,
    hasIsraeliOffice
  } = vc;

  const displayUrl = website?.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-gray-100 cursor-pointer group"
    >
      <div className="p-6">
        <div className="flex items-start space-x-4">
          {/* Logo or Fallback */}
          <div className="flex-shrink-0">
            <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
              <img
                src={imageUrl || getLogoUrl(vc.domain)}
                alt={`${name} logo`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden absolute inset-0 flex items-center justify-center bg-indigo-100 text-indigo-600 text-sm font-medium">
                {getFallbackInitials(name)}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold text-gray-900 truncate flex-1">
                {name}
                {hasIsraeliOffice && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                    IL Office
                  </span>
                )}
              </h2>
              <svg
                className="h-5 w-5 text-indigo-500 group-hover:text-indigo-600 transition-colors duration-200"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            <p className="mt-1 text-sm text-gray-500">
              {investment_stage && (
                <span className="text-gray-400">{investment_stage}</span>
              )}
            </p>

            <p className="mt-3 text-sm text-gray-600 line-clamp-3">
              {description}
            </p>
            {/* Investment Stats */}
            <div className="mt-3 flex space-x-4 text-sm text-gray-500">
              {num_investments > 0 && (
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                  </svg>
                  {num_investments} Investments
                </span>
              )}
              {num_exits > 0 && (
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                  </svg>
                  {num_exits} Exits
                </span>
              )}
            </div>

            {/* Sectors */}
            {sectors && sectors.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {sectors.slice(0, 3).map((sector) => (
                  <span key={sector} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {sector}
                  </span>
                ))}
              </div>
            )}

            {/* Website Link */}
            {website && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-indigo-500 hover:text-indigo-600 flex items-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                  </svg>
                  {displayUrl}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 